import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Scale,
  ExternalLink,
  Search,
  BookOpen,
  ShieldCheck,
  Download,
} from "lucide-react";
import {
  LEGISLATION,
  LEGISLATION_CATEGORIES,
  LAW_LIBRARY_RECENT,
  lawLibraryUrl,
  type ActEntry,
} from "@/lib/legislation-library";

function actSlug(a: ActEntry) {
  return `${a.title}-${a.number}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export const Route = createFileRoute("/legislation")({
  validateSearch: (s: Record<string, unknown>): { q?: string; cat?: string } => ({
    q: typeof s.q === "string" ? s.q : "",
    cat: typeof s.cat === "string" ? s.cat : "All",
  }),
  head: () => ({
    meta: [
      {
        title:
          "South African Legislation Library | Nkadimeng Danny Legal & Compliance",
      },
      {
        name: "description",
        content:
          "Free searchable library of South African Acts — Constitution, LRA, BCEA, POPIA, NCA, CPA, Cybercrimes Act and more. Read the full text on-site with search & pagination.",
      },
      {
        property: "og:title",
        content: "South African Legislation Library",
      },
      {
        property: "og:description",
        content:
          "Plain-language summaries and full text of South Africa's key Acts, with links to the official gov.za publication.",
      },
      { property: "og:type", content: "website" },
      {
        property: "og:url",
        content: "https://nkadimengdannylcs.com/legislation",
      },
    ],
    links: [
      { rel: "canonical", href: "https://nkadimengdannylcs.com/legislation" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "South African Legislation Library",
          url: "https://nkadimengdannylcs.com/legislation",
          publisher: {
            "@type": "Organization",
            name: "Nkadimeng Danny Legal & Compliance Support",
            url: "https://nkadimengdannylcs.com",
          },
          about: LEGISLATION_CATEGORIES.map((c) => ({
            "@type": "Thing",
            name: c,
          })),
          numberOfItems: LEGISLATION.length,
        }),
      },
    ],
  }),
  component: LegislationPage,
});

function LegislationPage() {
  const { q: initialQ, cat: initialCat } = Route.useSearch();
  const [query, setQuery] = useState(initialQ ?? "");
  const [category, setCategory] = useState<string>(initialCat ?? "All");


  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LEGISLATION.filter((a) => {
      if (category !== "All" && a.category !== category) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        (a.short ?? "").toLowerCase().includes(q) ||
        a.summary.toLowerCase().includes(q) ||
        a.number.toLowerCase().includes(q)
      );
    });
  }, [query, category]);

  const grouped = useMemo(() => {
    const map = new Map<string, ActEntry[]>();
    for (const a of filtered) {
      if (!map.has(a.category)) map.set(a.category, []);
      map.get(a.category)!.push(a);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="bg-gradient-to-b from-navy/5 to-transparent py-16">
        <div className="container-narrow">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            Free resource
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            South African Legislation Library
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            Plain-language summaries of {LEGISLATION.length}+ key South African
            Acts, organised by topic. Download a short <strong>fact sheet</strong>{" "}
            or the <strong>full text of the Act</strong> as a PDF — full-text
            files are fetched live from the official gov.za publication the first
            time they're requested and cached on our servers for fast delivery.
            Updated regularly — if an Act you need is missing,{" "}
            <Link to="/contact" className="text-navy underline">
              let us know
            </Link>
            .
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Acts (e.g. POPIA, eviction, dismissal)…"
                className="w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            >
              <option value="All">All categories</option>
              {LEGISLATION_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">
              {filtered.length} Act{filtered.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* LawLibrary recent-updates callout */}
          <div className="mt-8 rounded-xl border border-navy/20 bg-navy/5 p-5">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-navy text-navy-foreground">
                <BookOpen className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-lg text-navy">
                  Latest amendments &amp; new judgments
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  For the most recent <strong>consolidated Acts</strong> (with
                  amendments applied) and freshly reported South African
                  judgments, we recommend LawLibrary — a free public service by
                  AfricanLII / Laws.Africa.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    href={LAW_LIBRARY_RECENT.legislation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
                  >
                    Recent Acts <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={LAW_LIBRARY_RECENT.judgments}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-navy px-3 py-1.5 text-xs font-semibold text-navy hover:bg-navy hover:text-navy-foreground"
                  >
                    Recent judgments <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <a
                    href={LAW_LIBRARY_RECENT.home}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-navy px-3 py-1.5 text-xs font-semibold text-navy hover:bg-navy hover:text-navy-foreground"
                  >
                    Browse LawLibrary <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Library */}
      <section className="py-12">
        <div className="container-narrow space-y-12">
          {grouped.length === 0 && (
            <p className="text-center text-muted-foreground">
              No Acts match your search.
            </p>
          )}
          {grouped.map(([cat, acts]) => (
            <div key={cat}>
              <h2 className="mb-4 flex items-center gap-2 font-display text-2xl text-navy">
                <BookOpen className="h-5 w-5 text-gold" /> {cat}
              </h2>
              <div className="grid gap-4 md:grid-cols-2">
                {acts.map((a) => (
                  <article
                    key={`${a.title}-${a.number}`}
                    className="group rounded-xl border border-border bg-card p-5 transition hover:border-navy/40 hover:shadow-soft"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-navy/10 text-navy">
                        <Scale className="h-4 w-4" />
                      </div>
                      {a.short && (
                        <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-navy">
                          {a.short}
                        </span>
                      )}
                    </div>
                    <h3 className="mt-3 font-display text-base font-semibold text-navy">
                      {a.title}
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Act No. {a.number}
                    </p>
                    <p className="mt-3 text-sm text-foreground/80 leading-relaxed">
                      {a.summary}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                      <Link
                        to="/legislation/$slug"
                        params={{ slug: actSlug(a) }}
                        className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
                        title="Read the full text on this site with search & pagination"
                      >
                        <BookOpen className="h-3.5 w-3.5" /> Read on site
                      </Link>
                      <a
                        href={`/api/public/legislation?slug=${actSlug(a)}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-navy px-3 py-1.5 text-xs font-semibold text-navy hover:bg-navy hover:text-navy-foreground"
                      >
                        <Download className="h-3.5 w-3.5" /> Fact sheet
                      </a>
                      <a
                        href={`/api/public/legislation?slug=${actSlug(a)}&full=1`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-navy px-3 py-1.5 text-xs font-semibold text-navy hover:bg-navy hover:text-navy-foreground"
                        title="Full text fetched live from the official gov.za publication and cached on our servers."
                      >
                        <Download className="h-3.5 w-3.5" /> Full Act (PDF)
                      </a>
                      {a.url && (
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-medium text-navy hover:underline"
                        >
                          Full Act on gov.za
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      <a
                        href={lawLibraryUrl(a)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-medium text-navy hover:underline"
                        title="Consolidated version with amendments on LawLibrary"
                      >
                        Latest amendments (LawLibrary)
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy/5 py-12">
        <div className="container-narrow rounded-2xl border border-border bg-card p-8 text-center">
          <ShieldCheck className="mx-auto h-8 w-8 text-gold" />
          <h2 className="mt-3 font-display text-2xl text-navy">
            Need help applying these Acts?
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            Reading the Act is one thing; applying it to your facts is another.
            Book a consultation and we'll translate the statute into a clear
            action plan.
          </p>
          <Link
            to="/book"
            className="mt-5 inline-flex items-center justify-center rounded-md bg-navy px-5 py-2.5 text-sm font-medium text-navy-foreground shadow-soft hover:bg-navy/90"
          >
            Book a consultation
          </Link>
        </div>
      </section>
    </div>
  );
}
